import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import LectureCard from "@/components/LectureCard";
import TrendingStrip from "@/components/TrendingStrip";
import { Button } from "@/components/ui/button";
import { GraduationCap, Mic2, MessagesSquare, Building2, ArrowRight, BookOpen } from "lucide-react";

export default function Landing() {
  const [upcoming, setUpcoming] = useState([]);
  const [recorded, setRecorded] = useState([]);
  const [stats, setStats] = useState({ users: 0, lectures: 0, recorded: 0 });
  const [plans, setPlans] = useState({ premium_enabled: true });

  useEffect(() => {
    api.get("/lectures?upcoming=true").then(({ data }) => setUpcoming(data.slice(0, 3))).catch(() => {});
    api.get("/lectures?recorded=true").then(({ data }) => setRecorded(data.slice(0, 3))).catch(() => {});
    api.get("/dashboard/global").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/billing/plans").then(({ data }) => setPlans(data)).catch(() => {});
  }, []);

  return (
    <div data-testid="landing-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-navy via-navy-700 to-teal-700" aria-hidden />
        <div className="absolute inset-0 opacity-30 mix-blend-screen" style={{
          backgroundImage:
            "url(https://images.pexels.com/photos/19193975/pexels-photo-19193975.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)",
          backgroundSize: "cover", backgroundPosition: "center",
        }} aria-hidden />
        <div className="relative et-section py-20 lg:py-28 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-12 text-white animate-fade-up">
            <div className="et-label text-teal-50/80 mb-4">A new academic public square</div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-4xl">
              Where <span className="text-teal-50">scholarship</span> meets <span className="italic">conversation</span>.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-white/85 max-w-3xl leading-relaxed">
              Talkademia is the digital architecture for a borderless intellect. We are replacing the
              one-way lecture with a living network of real-time inquiry. By dissolving campus walls,
              we've created a global common room where scholars converge to meet, challenge, and build
              the future of knowledge through live, collective dialogue.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register" data-testid="hero-cta-register"><Button size="lg" className="bg-teal hover:bg-teal-700 text-white rounded-md h-12 px-6">Join Talkademia <ArrowRight className="h-4 w-4 ml-1.5"/></Button></Link>
              <Link to="/upcoming" data-testid="hero-cta-browse"><Button size="lg" variant="outline" className="bg-white/10 hover:bg-white/20 border-white/30 text-white rounded-md h-12 px-6">Browse Upcoming</Button></Link>
            </div>
            <div className="mt-12 grid grid-cols-3 gap-6 max-w-md">
              <Stat n={stats.users} label="Scholars"/>
              <Stat n={stats.lectures} label="Sessions"/>
              <Stat n={stats.recorded} label="Recorded"/>
            </div>
          </div>
        </div>
      </section>

      {/* WHO IS IT FOR */}
      <section className="et-section py-20">
        <div className="et-label mb-3">Built for academia</div>
        <h2 className="font-serif text-3xl lg:text-4xl text-navy max-w-2xl">Four account types, one shared scholarly commons.</h2>
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Pillar icon={Building2} title="Universities" body="Host departmental seminars, public lectures and recurring colloquia. Showcase faculty and grow attendance."/>
          <Pillar icon={GraduationCap} title="Research Centers" body="Convene roundtables across institutions. Invite specific speakers, set criteria, and archive every session."/>
          <Pillar icon={Mic2} title="Individual Scholars" body="Lead discussions on your terms — open, gated by approval, or invite-only. Q&A built in."/>
          <Pillar icon={BookOpen} title="Journals" body="Announce new releases, host editor roundtables, and connect with the scholars who cite, review, and submit to your journal."/>
        </div>
      </section>

      {/* TIERS */}
      <section className="bg-slate-100/60 border-y border-slate-200">
        <div className="et-section py-20 grid lg:grid-cols-2 gap-8">
          <Tier
            title="Free"
            price="$0"
            tag="Get started"
            features={[
              "Attend any open lecture",
              "Host sessions up to 60 minutes",
              "Up to 20 attendees per session",
              "Built-in Q&A discussion thread",
              "Attendees can use camera & microphone",
            ]}
          />
          <Tier
            featured
            title="Premium"
            price={plans.premium_enabled ? "$9" : null}
            tag={plans.premium_enabled ? "For active hosts" : "Coming soon"}
            comingSoon={!plans.premium_enabled}
            futurePriceMo="$9"
            futurePriceYr="$98"
            features={[
              "Unlimited lecture duration",
              "Unlimited attendees per session",
              "Open roundtables and panels",
              "Recurring schedules & invited speakers",
              "Optional paid registration with custom fees",
              "Certificates of attendance issued by the host",
            ]}
          />
        </div>
      </section>

      {/* UPCOMING */}
      <section className="et-section py-20">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="et-label mb-2">Live & scheduled</div>
            <h2 className="font-serif text-3xl text-navy">Upcoming sessions</h2>
          </div>
          <Link to="/upcoming" className="text-sm text-teal hover:text-teal-700 font-medium inline-flex items-center" data-testid="view-all-upcoming">View all <ArrowRight className="h-4 w-4 ml-1"/></Link>
        </div>
        {upcoming.length ? (
          <div className="grid md:grid-cols-3 gap-6">{upcoming.map(l => <LectureCard key={l.id} lecture={l}/>)}</div>
        ) : (
          <EmptyHint label="No upcoming sessions yet — be the first to host."/>
        )}
      </section>

      {/* RECORDED */}
      <section className="et-section pb-20">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="et-label mb-2">On demand</div>
            <h2 className="font-serif text-3xl text-navy">Recorded archive</h2>
          </div>
          <Link to="/recorded" className="text-sm text-teal hover:text-teal-700 font-medium inline-flex items-center" data-testid="view-all-recorded">View all <ArrowRight className="h-4 w-4 ml-1"/></Link>
        </div>
        {recorded.length ? (
          <div className="grid md:grid-cols-3 gap-6">{recorded.map(l => <LectureCard key={l.id} lecture={l}/>)}</div>
        ) : (
          <EmptyHint label="The archive is empty — recorded lectures will appear here." />
        )}
      </section>

      {/* TRENDING ON CONVERSE */}
      <section className="et-section pb-20">
        <TrendingStrip />
      </section>

      {/* In-page footer is now provided globally by <Footer /> in App.js */}
    </div>
  );
}

function Stat({ n, label }) {
  return (
    <div>
      <div className="text-3xl font-serif text-white">{n}</div>
      <div className="text-xs uppercase tracking-[0.2em] text-white/60 mt-1">{label}</div>
    </div>
  );
}

function Pillar({ icon: Icon, title, body }) {
  return (
    <div className="et-card p-7" data-testid={`pillar-${title}`}>
      <div className="h-11 w-11 rounded-md bg-teal-50 text-teal flex items-center justify-center"><Icon className="h-5 w-5"/></div>
      <h3 className="font-serif text-xl text-navy mt-5">{title}</h3>
      <p className="text-sm text-slate-600 mt-2 leading-relaxed">{body}</p>
    </div>
  );
}

function Tier({ title, price, tag, features, featured, comingSoon, futurePriceMo, futurePriceYr }) {
  return (
    <div className={`p-8 rounded-2xl border ${featured ? "bg-navy text-white border-navy" : "bg-white border-slate-200"}`} data-testid={`tier-${title.toLowerCase()}`}>
      <div className={`text-xs font-semibold uppercase tracking-[0.2em] ${featured ? "text-teal-50/80" : "text-slate-500"}`}>{tag}</div>
      <div className="mt-3 flex items-baseline gap-2 flex-wrap">
        <h3 className="font-serif text-3xl">{title}</h3>
        {comingSoon ? (
          <>
            <div className={`text-sm ${featured ? "text-white/70" : "text-slate-500"} italic`} data-testid={`tier-${title.toLowerCase()}-coming-soon`}>· launching soon</div>
            {futurePriceMo && (
              <div className={`text-sm font-semibold ${featured ? "text-teal-50" : "text-teal"}`} data-testid={`tier-${title.toLowerCase()}-future-price`}>
                · {futurePriceMo}/mo{futurePriceYr ? ` or ${futurePriceYr}/yr` : ""}
              </div>
            )}
          </>
        ) : price ? (
          <div className={`text-sm ${featured ? "text-white/70" : "text-slate-500"}`}>· {price}/mo</div>
        ) : null}
      </div>
      {comingSoon && (
        <p className={`mt-3 text-sm leading-relaxed ${featured ? "text-white/85" : "text-slate-600"}`}>
          We're refining Premium. <strong>Every hosting feature is free for now.</strong>
          {" "}Sign in and tap your profile to be notified the day it opens.
        </p>
      )}
      <ul className={`mt-6 space-y-3 text-sm ${featured ? "text-white/90" : "text-slate-700"}`}>
        {features.map(f => (
          <li key={f} className="flex items-start gap-2">
            <span className={`mt-1 h-1.5 w-1.5 rounded-full ${featured ? "bg-teal-50" : "bg-teal"}`}/>
            <span>{f}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function EmptyHint({ label }) {
  return <div className="border border-dashed border-slate-300 rounded-xl p-10 text-center text-slate-500" data-testid="empty-hint">{label}</div>;
}
